/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag
 */
import { type CSSProperties, useEffect, useState } from 'react';

import { css as cssUnbounded, jsx, keyframes } from '@compiled/react';

import { token } from '@atlaskit/tokens';

// Stagger delay per icon in ms
const STAGGER_DELAY_MS = 300;

const iconFanIn = keyframes({
	from: {
		opacity: 0,
		scale: '0.5',
	},
	to: {
		opacity: 1,
		scale: '1',
	},
});

const fanContainerStyle = cssUnbounded({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	flexShrink: 0,
});

const iconWrapperBaseStyle = cssUnbounded({
	width: '24px',
	height: '24px',
	borderRadius: '6px',
	overflow: 'hidden',
	border: `0.821px solid ${token('color.border')}`,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	marginLeft: '-4px',
});

const iconWrapperAnimatedStyle = cssUnbounded({
	animation: `${iconFanIn} 250ms ease-out both`,
});

const firstIconStyle = cssUnbounded({
	marginLeft: '0',
});

const imgStyle = cssUnbounded({
	display: 'block',
});

const overflowPillBaseStyle = cssUnbounded({
	width: '24px',
	height: '24px',
	borderRadius: '6px',
	border: `0.667px solid ${token('color.border')}`,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	marginLeft: '-4px',
	// Intentionally hardcoded to match light-mode appearance in both themes
	// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
	backgroundColor: '#FFFFFF',
	// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
	color: '#626F86',
	font: token('font.body.small'),
	position: 'relative',
});

const overflowPillAnimatedStyle = cssUnbounded({
	animation: `${iconFanIn} 250ms ease-out both`,
});

// Max icons to display before showing overflow pill
const MAX_ICONS = 4;

// Rotation pattern per icon position: 0, -6, 0, 8
const rotationAngles = ['0deg', '-6deg', '0deg', '8deg'];

// Module-level state to persist across mount/unmount cycles (e.g. when toggling between
// the collapsed preview and the expanded view). Keyed by stepId.
type FanState = {
	revealed: Set<string>;
	revealedOverflow: boolean;
	animated: Set<string>;
	animatedOverflow: boolean;
};
const fanStateByStepId = new Map<string, FanState>();
const getFanState = (stepId: string): FanState => {
	let state = fanStateByStepId.get(stepId);
	if (!state) {
		state = {
			revealed: new Set(),
			revealedOverflow: false,
			animated: new Set(),
			animatedOverflow: false,
		};
		fanStateByStepId.set(stepId, state);
	}
	return state;
};

type SourceIconsFanProps = {
	icons: string[] | undefined;
	isInProgress?: boolean;
	stepId: string;
};

export const SourceIconsFan = ({
	icons,
	isInProgress,
	stepId,
}: SourceIconsFanProps): JSX.Element | null => {
	const targetIcons = icons ? icons.slice(0, MAX_ICONS) : [];
	const targetOverflowCount = icons ? icons.length - MAX_ICONS : 0;

	// Persist animation state at the module level (keyed by stepId) so that
	// toggling between the collapsed preview and expanded view doesn't replay the animation.
	const fanState = getFanState(stepId);
	// Force re-render when we add to the revealed sets.
	const [, forceUpdate] = useState(0);

	// Use a tick counter that grows after each reveal — this is what the effect actually depends on.
	const [revealTick, setRevealTick] = useState(0);
	const targetIconsKey = targetIcons.join('|');

	// Stagger reveal: every STAGGER_DELAY_MS, add the next not-yet-revealed icon.
	useEffect(() => {
		// Find the next icon URL that hasn't been revealed yet.
		const nextIcon = targetIcons.find((url) => !fanState.revealed.has(url));
		const allIconsRevealed = !nextIcon;
		const overflowNeeded = targetOverflowCount > 0 && !fanState.revealedOverflow;

		if (!nextIcon && !overflowNeeded) {
			return;
		}

		const timeout = setTimeout(() => {
			if (nextIcon) {
				fanState.revealed.add(nextIcon);
			} else if (allIconsRevealed && overflowNeeded) {
				fanState.revealedOverflow = true;
			}
			setRevealTick((n) => n + 1);
		}, STAGGER_DELAY_MS);
		return () => clearTimeout(timeout);
		// targetIcons is recomputed each render but its content is captured via targetIconsKey.
		// revealTick triggers a re-run after each successful reveal.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [targetIconsKey, targetOverflowCount, revealTick]);

	if (!icons || icons.length === 0) {
		return null;
	}

	// Visible icons: only those that have been revealed
	const visibleIcons = targetIcons.filter((url) => fanState.revealed.has(url));
	const showOverflowPill = targetOverflowCount > 0 && fanState.revealedOverflow;
	const overflowCount = targetOverflowCount;

	return (
		<div css={fanContainerStyle}>
			{visibleIcons.map((iconUrl, index) => {
				const rotation = rotationAngles[index % rotationAngles.length];
				const hasAnimated = fanState.animated.has(iconUrl);
				const iconStyle: CSSProperties = {
					transform: `rotate(${rotation})`,
				};
				return (
					<div
						key={iconUrl}
						css={[
							iconWrapperBaseStyle,
							index === 0 && firstIconStyle,
							!hasAnimated && iconWrapperAnimatedStyle,
						]}
						onAnimationEnd={() => {
							fanState.animated.add(iconUrl);
							forceUpdate((n) => n + 1);
						}}
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
						style={iconStyle}
					>
						<img src={iconUrl} alt="" width={24} height={24} css={imgStyle} />
					</div>
				);
			})}
			{showOverflowPill && (
				<div
					css={[overflowPillBaseStyle, !fanState.animatedOverflow && overflowPillAnimatedStyle]}
					onAnimationEnd={() => {
						fanState.animatedOverflow = true;
						forceUpdate((n) => n + 1);
					}}
				>
					{`+${overflowCount}`}
				</div>
			)}
		</div>
	);
};
